import { Loader2, Search, SlidersHorizontal, X } from 'lucide-react';
import type { PromotedListingSort, RiskLevel } from '@/client/types/promotion';
import { RISK_META } from './listingMetrics';
import { isFiltered as hasFilters, type ListingFilters } from './filters';

const RISKS: RiskLevel[] = ['high', 'medium', 'low'];

const ACTIVITY_OPTS: { id: ListingFilters['activity']; label: string }[] = [
  { id: 'all', label: 'All activity' },
  { id: 'active', label: 'Has active promo' },
  { id: 'scheduled', label: 'Scheduled only' },
  { id: 'inactive', label: 'No active promo' },
];

const SORT_OPTS: { id: PromotedListingSort; label: string }[] = [
  { id: 'risk', label: 'Risk (high → low)' },
  { id: 'fill', label: 'Fill rate (low → high)' },
  { id: 'impressions', label: 'Impressions (high → low)' },
  { id: 'spend', label: 'Total spend (high → low)' },
  { id: 'packages', label: 'Packages (most first)' },
  { id: 'recent', label: 'Most recent spend' },
];

export const ListingsToolbar: React.FC<{
  filters: ListingFilters;
  onChange: (next: ListingFilters) => void;
  /** Raw search text — debounced by the page before it reaches `filters.query`. */
  search: string;
  onSearch: (value: string) => void;
  types: string[];
  /** Server-reported match count for the current filters. */
  resultCount: number;
  isFetching?: boolean;
  onReset: () => void;
}> = ({ filters, onChange, search, onSearch, types, resultCount, isFetching, onReset }) => {
  const toggleRisk = (r: RiskLevel) =>
    onChange({
      ...filters,
      risk: filters.risk.includes(r) ? filters.risk.filter((x) => x !== r) : [...filters.risk, r],
    });

  const filtered = hasFilters(filters);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search by listing, owner, or EID…"
            className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <select
          value={filters.type}
          onChange={(e) => onChange({ ...filters, type: e.target.value })}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All types</option>
          {types.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>

        <select
          value={filters.activity}
          onChange={(e) => onChange({ ...filters, activity: e.target.value as ListingFilters['activity'] })}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {ACTIVITY_OPTS.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>

        <div className="flex items-center gap-1.5 text-sm text-gray-500">
          <SlidersHorizontal className="h-4 w-4" />
          <select
            value={filters.sort}
            onChange={(e) => onChange({ ...filters, sort: e.target.value as PromotedListingSort })}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {SORT_OPTS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-400">Risk</span>
        {RISKS.map((r) => {
          const active = filters.risk.includes(r);
          const m = RISK_META[r];
          return (
            <button
              key={r}
              type="button"
              onClick={() => toggleRisk(r)}
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset transition-colors ${
                active ? m.badge : 'bg-white text-gray-500 ring-gray-200 hover:bg-gray-50'
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
              {m.label}
            </button>
          );
        })}

        <div className="ml-auto flex items-center gap-3 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1.5">
            <span className="font-semibold text-gray-900">{resultCount.toLocaleString()}</span>
            {filtered ? 'matching listings' : 'promoted listings'}
            {isFetching && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
          </span>
          {filtered && (
            <button
              onClick={onReset}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 font-medium text-gray-600 hover:bg-gray-100"
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
